import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useChatContext } from "../ChatContext";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const BUILT_IN_FIELDS = [
    "id", "createdAt", "updatedAt", "createdBy",
    "username", "email", "password", "firstName",
    "lastName", "isActive",
];

export default function useUsersPanelData(workflowId) {
    const { t } = useTranslation();
    const { confirm, viewAsUser, setViewAsUser } = useChatContext();

    const [users, setUsers] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [userEntitySchema, setUserEntitySchema] = useState(null);

    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [roleFilter, setRoleFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formError, setFormError] = useState(null);

    const searchTimerRef = useRef(null);

    const { call: getUsersApi } = useBluelagoon({ method: "getWorkflowUsers" });
    const { call: createUserApi } = useBluelagoon({ method: "createWorkflowUser" });
    const { call: updateUserApi } = useBluelagoon({ method: "updateWorkflowUser" });
    const { call: deleteUserApi } = useBluelagoon({ method: "deleteWorkflowUser" });
    const { call: getUserEntitySchemaApi } = useBluelagoon({ method: "getWorkflowUserEntitySchema" });
    const { call: generateTokenApi } = useBluelagoon({ method: "generateWorkflowUserToken" });

    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchQuery(value);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(value);
            setCurrentPage(1);
        }, SEARCH_DEBOUNCE_MS);
    }, []);

    useEffect(() => {
        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        };
    }, []);

    const fetchUsers = useCallback(async () => {
        if (!workflowId) return;
        setLoading(true);
        setError(null);
        try {
            const response = await getUsersApi({
                workflowId,
                search: debouncedSearch,
                limit: PAGE_SIZE,
                offset: (currentPage - 1) * PAGE_SIZE,
                status: statusFilter,
                role: roleFilter,
            });
            setUsers(response?.data?.users || []);
            setPagination(response?.data?.pagination || null);
        } catch (err) {
            setError(err.message || t("workflowUsers.failedToFetchUsers"));
        } finally {
            setLoading(false);
        }
    }, [workflowId, getUsersApi, debouncedSearch, currentPage, statusFilter, roleFilter, t]);

    const fetchUserEntitySchema = useCallback(async () => {
        if (!workflowId) return;
        try {
            const response = await getUserEntitySchemaApi({ workflowId });
            setUserEntitySchema(
                response?.data?.exists && response?.data?.schema ? response.data.schema : null
            );
        } catch {
            setUserEntitySchema(null);
        }
    }, [workflowId, getUserEntitySchemaApi]);

    useEffect(() => {
        if (workflowId) {
            fetchUsers();
            fetchUserEntitySchema();
        }
    }, [workflowId, fetchUsers, fetchUserEntitySchema]);

    const configKeys = useMemo(() => {
        if (!userEntitySchema?.properties) return [];
        return Object.keys(userEntitySchema.properties).filter(
            (key) => !BUILT_IN_FIELDS.includes(key)
        );
    }, [userEntitySchema]);

    const getFieldConfig = useCallback((fieldKey) => {
        const field = userEntitySchema?.properties?.[fieldKey];
        if (!field) return { label: fieldKey, type: "text", placeholder: fieldKey, required: false };
        return {
            label: fieldKey,
            type: field.type || "text",
            placeholder: field.description || fieldKey,
            required: userEntitySchema.required?.includes(fieldKey) || false,
            maxLength: field.maxLength,
            format: field.format,
            enum: field.enum || null,
        };
    }, [userEntitySchema]);

    const handleEditUser = useCallback((user) => {
        setEditingUser(user);
        setFormError(null);
        setIsDialogOpen(true);
    }, []);

    const handleCreateUser = useCallback(() => {
        setEditingUser(null);
        setFormError(null);
        setIsDialogOpen(true);
    }, []);

    const handleDeleteUser = useCallback(async (user) => {
        try {
            await confirm({
                title: t("workflowUsers.deleteUserTitle"),
                description: t("workflowUsers.deleteUserDescription", { username: user.username }),
                confirmText: t("workflowUsers.deleteButton"),
                cancelText: t("workflowUsers.cancel"),
            });
            await deleteUserApi({ workflowId, workflowUserId: user.id });
            toast.success(t("workflowUsers.userDeleted"));
            fetchUsers();
        } catch (error) {
            if (error.message !== "User canceled") {
                toast.error(t("workflowUsers.failedToDeleteUser"));
            }
        }
    }, [confirm, deleteUserApi, workflowId, fetchUsers, t]);

    const handleViewAsUser = useCallback(async (user) => {
        try {
            const response = await generateTokenApi({ workflowId, workflowUserId: user.id });
            if (response?.data?.token) {
                setViewAsUser({ id: user.id, username: user.username, token: response.data.token });
                toast.success(t("workflowUsers.viewingAs", { username: user.username }));
            }
        } catch {
            toast.error(t("workflowUsers.failedToGenerateToken"));
        }
    }, [generateTokenApi, workflowId, setViewAsUser]);

    const handleToggleBlock = useCallback(async (user) => {
        const isBlocking = user.isActive;
        try {
            await confirm({
                title: t(isBlocking ? "workflowUsers.blockUserTitle" : "workflowUsers.unblockUserTitle"),
                description: t(isBlocking ? "workflowUsers.blockUserDescription" : "workflowUsers.unblockUserDescription", { username: user.username }),
                confirmText: t(isBlocking ? "workflowUsers.blockUser" : "workflowUsers.unblockUser"),
                cancelText: t("workflowUsers.cancel"),
            });
            await updateUserApi({ workflowId, workflowUserId: user.id, isActive: !isBlocking });
            toast.success(t(isBlocking ? "workflowUsers.userBlocked" : "workflowUsers.userUnblocked"));
            fetchUsers();
        } catch (error) {
            if (error.message !== "User canceled") {
                toast.error(t("workflowUsers.failedToUpdateUser"));
            }
        }
    }, [confirm, updateUserApi, workflowId, fetchUsers, t]);

    const handleFormSubmit = useCallback(async (formData, editingUser) => {
        setFormError(null);
        if (formData.password && (formData.password.length < 7 || !/[A-Z]/.test(formData.password) || !/[0-9]/.test(formData.password))) {
            setFormError(t("workflowUsers.passwordRequirements"));
            return;
        }
        try {
            const userData = {
                workflowId,
                username: formData.username,
                email: formData.email,
                firstName: formData.firstName,
                lastName: formData.lastName,
                roles: [formData.role],
                isActive: formData.isActive,
                config: formData.config,
            };
            if (formData.password) userData.password = formData.password;

            if (editingUser) {
                await updateUserApi({ ...userData, workflowUserId: editingUser.id });
                toast.success(t("workflowUsers.userUpdated"));
            } else {
                await createUserApi(userData);
                toast.success(t("workflowUsers.userCreated"));
            }
            setIsDialogOpen(false);
            fetchUsers();
        } catch (error) {
            const msg = error?.error || error?.response?.data?.error || error?.message ||
                (editingUser ? t("workflowUsers.failedToUpdateUser") : t("workflowUsers.failedToCreateUser"));
            setFormError(msg);
            toast.error(msg);
        }
    }, [workflowId, updateUserApi, createUserApi, fetchUsers, t]);

    const handleStatusFilterChange = useCallback((value) => {
        setStatusFilter(value);
        setCurrentPage(1);
    }, []);

    const handleRoleFilterChange = useCallback((value) => {
        setRoleFilter(value);
        setCurrentPage(1);
    }, []);

    const totalPages = pagination ? Math.ceil(pagination.total / PAGE_SIZE) : 0;

    return {
        users, pagination, loading, error, userEntitySchema,
        searchQuery, statusFilter, roleFilter, currentPage, totalPages,
        isDialogOpen, editingUser, formError, configKeys,
        viewAsUser, setViewAsUser,
        setIsDialogOpen, setCurrentPage,
        handleSearchChange, handleStatusFilterChange, handleRoleFilterChange,
        handleCreateUser, handleEditUser, handleDeleteUser,
        handleViewAsUser, handleToggleBlock, handleFormSubmit,
        getFieldConfig,
    };
}
